import { useCallback, useEffect } from "react"

import { Col, Flex, Row, Spin, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"

import SelectComponent from "../../../../Components/Select"
import utils from "../../../../Utils/utils"
import Creators from "../../reducer"
import { preventionEmptyData } from "../../utils/emptyData"
import { useBusinessAccountId } from "../../VAMP/hooks/useBusinessAccountId"

import "./styles.less"

const { Title, Text } = Typography

const PreventionRate = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const isMobile = useSelector((state) => state.nav.isMobile)
  const { dataSource } = useSelector((state) => state.health)
  const { is_business_health_available } = useSelector((state) => state.health.mainInfo)
  const years = useSelector((state) => state.health?.filterOptions?.years) || []
  const preventionRateYear =
    useSelector((state) => state.health.preventionRateYear) || new Date().getFullYear()
  const loading = useSelector((state) => state.health.preventionRateFetching) || false
  let data = useSelector((state) => state.health.preventionRateData) || []
  if (!is_business_health_available) data = preventionEmptyData
  const yearOptions = years?.map((year) => ({ label: year, value: year }))
  const business_account = useBusinessAccountId()

  useEffect(() => {
    dispatch(
      Creators.getPreventionRateRequest({
        year: preventionRateYear,
        business_account,
        source: dataSource
      })
    )
  }, [preventionRateYear, business_account, dataSource])

  useEffect(() => {
    if (years?.length && !years?.includes(preventionRateYear)) {
      handleYearChange(years[0])
    }
    if (!years?.length) {
      handleYearChange(null)
    }
  }, [years])

  const handleYearChange = useCallback((year) => {
    dispatch(Creators.healthSetProp("preventionRateYear", year))
  }, [])

  const renderMonthColumn = (monthData) => {
    const {
      month = "",
      preventionRate = 94.43,
      disputes_count = 158,
      alerts_count = 1200
    } = monthData

    if (isMobile)
      return (
        <div className="month-tile">
          <MonthStats
            month={month}
            preventionRate={preventionRate}
            disputes_count={disputes_count}
            alerts_count={alerts_count}
          />
        </div>
      )

    return (
      <Col key={month} className="month-column">
        <MonthStats
          month={month}
          preventionRate={preventionRate}
          disputes_count={disputes_count}
          alerts_count={alerts_count}
        />
      </Col>
    )
  }

  return (
    <Flex gap={16} vertical={true} className="prevention-rate-card">
      <Flex gap={isMobile ? 12 : 24} justify="space-between" vertical={isMobile}>
        <Flex gap={8} vertical={true}>
          <Title level={isMobile ? 5 : 3} style={{ margin: 0, textAlign: "left" }}>
            {t("Chargeback Prevention Rate")}
          </Title>
          <Text type="secondary">{t("View your chargeback prevention performance by month.")}</Text>
        </Flex>
        <SelectComponent
          size="large"
          value={preventionRateYear}
          onChange={handleYearChange}
          options={yearOptions}
          style={{ width: isMobile ? "100%" : 100 }}
        />
      </Flex>

      <Spin spinning={loading}>
        <div style={{ padding: "0 4px" }}>
          {!isMobile && (
            <div style={{ padding: "0 4px" }}>
              <Row gutter={[8, 16]} justify="start" className="months-container">
                {data?.map((monthData) => renderMonthColumn(monthData))}
              </Row>
            </div>
          )}
          {isMobile && (
            <div className="months-container-mobile">
              {data?.map((monthData) => renderMonthColumn(monthData))}
            </div>
          )}
        </div>
      </Spin>
    </Flex>
  )
}

export default PreventionRate

const MonthStats = ({ month, preventionRate, disputes_count, alerts_count }) => {
  const { t } = useTranslation()

  return (
    <Flex gap={12} vertical={true}>
      <Text strong>{month}</Text>

      <Flex gap={2} vertical={true} className="month-stats">
        <Text strong>{preventionRate}%</Text>

        <Flex gap={4}>
          <Text strong>{utils.convertNumber(disputes_count, 1)}</Text>
          <Text type="secondary">{t("disputes")}</Text>
        </Flex>

        <Flex gap={4}>
          <Text strong>{utils.convertNumber(alerts_count, 1)}</Text>
          <Text type="secondary" style={{ whiteSpace: "nowrap" }}>
            {t("prevented")}
          </Text>
        </Flex>
      </Flex>
    </Flex>
  )
}
